import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Heading6 } from '@ui/Typography'
import { Accordion } from '@ui/components/Accordion'
import { Input } from '@ui/components/Input'
import { RichIcon } from '@ui/components/RichIcon'
import { Tooltip } from '@ui/components/Tooltip'
import { CheckIcon } from '@ui/icons/Check.icon'
import { ExternalLinkIcon } from '@ui/icons/ExternalLink.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { extractDigitsFromString } from '@utils/formValidation'

import { SocialMediaTypes } from '@model/WalletPaymentAgentProfileDto'
import { WalletPaymentAgentTransferInformationDto } from '@model/WalletPaymentAgentTransferInformationDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { InstagramIcon } from '@assets/socials/Instagram.icon'
import { MessengerIcon } from '@assets/socials/Messenger.icon'
import { TelegramIcon } from '@assets/socials/Telegram.icon'
import { WhatsAppIcon } from '@assets/socials/WhatsApp.icon'

import { Box } from '@shared/components/Box'
import { Notice } from '@shared/components/Notice'

export const useAgentInformationFetch = (walletId?: string, paymentAgentId?: string) => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const isEnabled = Boolean(walletId && paymentAgentId)

  const { data: paymentAgent, isLoading } = useQuery({
    queryKey: queryKeys.paymentAgent.transferInfo(walletId, paymentAgentId),
    queryFn: () =>
      apiClient.getPaymentAgentsTransferInformation(walletId as string, paymentAgentId as string),
    enabled: isEnabled,
  })

  const forceRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.paymentAgent.transferInfo(walletId, paymentAgentId),
    })
  }

  return {
    paymentAgent,
    isLoading: isEnabled ? isLoading : false,
    forceRefresh,
  }
}

export enum AgentInformationType {
  Deposit,
  Withdraw,
}

const isDeposit = (props: AgentInformationDetailsProps) => {
  return props?.type === AgentInformationType.Deposit
}

interface AgentInformationProps {
  type: AgentInformationType
  paymentAgent: WalletPaymentAgentTransferInformationDto
}

enum PaymentMethodType {
  Depomost = 'depomost',
}

const isDepomost = (props: AgentInformationDetailsProps) => {
  return props?.paymentAgent?.externalName?.toLowerCase() === PaymentMethodType.Depomost
}

export const AgentInformation: React.FC<AgentInformationProps> = (props) => {
  const { paymentAgent } = props
  const { t } = useTranslation()

  return (
    paymentAgent && (
      <Box fullWidth flex direction='column' gap='x16'>
        <Box flex align='center' gap='x8' justify='space-between'>
          <Heading6 medium>{t('Payment Agent.Agent information')}</Heading6>
          <Tooltip content={t('Payment Agent.Verified Agent')}>
            <RichIcon
              shape='circle'
              icon={{ icon: CheckIcon, color: 'text-white' }}
              size={'x24'}
              bg='surface-success-high-em'
            />
          </Tooltip>
        </Box>

        {paymentAgent.powerOfAttorney.accepted && (
          <AgentInformationDetails type={props.type} paymentAgent={paymentAgent} />
        )}
      </Box>
    )
  )
}

interface AgentInformationDetailsProps {
  type: AgentInformationProps['type']
  paymentAgent: WalletPaymentAgentTransferInformationDto
}

const SocialMediaIcons: Record<Partial<number>, ReactElement<any>> = {
  [SocialMediaTypes.WhatsApp]: <WhatsAppIcon />,
  [SocialMediaTypes.Facebook]: <MessengerIcon />,
  [SocialMediaTypes.Telegram]: <TelegramIcon />,
  [SocialMediaTypes.Instagram]: <InstagramIcon />,
}

const socialMediaIconsOrder = [
  SocialMediaTypes.WhatsApp,
  SocialMediaTypes.Telegram,
  SocialMediaTypes.Instagram,
  SocialMediaTypes.Facebook,
]

export const AgentInformationDetails: React.FC<AgentInformationDetailsProps> = (props) => {
  const { paymentAgent } = props
  const { formatMoney } = useFormatNumber()
  const { t } = useTranslation()

  return (
    <Box flex direction='column' gap='x16'>
      {!!paymentAgent.website && (
        <Input
          rightIcon={ExternalLinkIcon}
          onEndIconClick={() => window.open(paymentAgent.website, '_blank')}
          readOnly
          value={paymentAgent.website}
          label={t('Payment Agent.Website')}
        />
      )}
      {isDeposit(props) && <InfoNote {...props} />}
      <Input
        readOnly
        value={formatMoney(paymentAgent.wallet.balance, paymentAgent.wallet.currency.id)}
        label={t('Payment Agent.Agent Balance')}
        hint={
          props.type === AgentInformationType.Deposit
            ? t('Payment Agent.Current max deposit amount')
            : t('Payment Agent.Current max withdrawal amount')
        }
      />
      <Accordion fullWidth title={t('Payment Agent.Who are Payment Agents?')}>
        <Body1 isParagraph>{t('Payment Agent.Payment Agents are authorised persons who')}</Body1>
      </Accordion>

      {paymentAgent.profiles.map((x, index) => {
        const digitNumber = extractDigitsFromString(x.phone ?? '')
        const phoneNumber = digitNumber ? '+' + digitNumber : ''
        const sortedSocialMediaLinks = x.socialMedia?.sort((a, b) => {
          return (
            socialMediaIconsOrder.indexOf(a.socialMediaType.id) -
            socialMediaIconsOrder.indexOf(b.socialMediaType.id)
          )
        })

        return (
          <Accordion fullWidth title={`${t('Payment Agent.Responsible person')} #${index + 1}`}>
            <Input
              readOnly
              value={`${x.firstName} ${x.lastName}`}
              label={t('Payment Agent.Full Name')}
              renderRightIconJSX={
                <Box flex align='center' justify='center' gap='x8'>
                  {sortedSocialMediaLinks?.map((link) => (
                    <a href={link.value ?? '#'} target='_blank' key={link.id} rel='noreferrer'>
                      {SocialMediaIcons[link.socialMediaType.id]}
                    </a>
                  ))}
                </Box>
              }
            />
            <Input canCopy readOnly value={x.email?.toString()} label={t('Payment Agent.Email')} />
            <Input canCopy readOnly value={phoneNumber} label={t('Payment Agent.Phone')} />
          </Accordion>
        )
      })}
    </Box>
  )
}

const InfoNote: React.FC<AgentInformationDetailsProps> = (props) => {
  const { t } = useTranslation()

  const text = isDepomost(props)
    ? t('Payment Agent.Please continue to the Payment Agent website')
    : t('Payment Agent.Please contact the Payment Agent directly')

  return <Notice id={'wallet-agent-info'} type={'info'} message={text} />
}
